import React, {useContext, useEffect, useState} from "react";
import {AiExtractorServiceContext, AiModelPricingServiceContext, AppContextContext} from "../../Contexts";
import {AutoComplete, Breadcrumb, Button, Descriptions, Form, Input, InputNumber, message, Modal, Select, Skeleton, Spin} from "antd";
import {LoadingOutlined} from "@ant-design/icons";
import Collapse from "@kunukn/react-collapse";
import * as globalStyles from "../App.module.css";
import {Link, useNavigate, useParams} from "react-router-dom";
import {DocumentTitle} from "../DocumentTitle";
import AiModelPricing from "../../domain/AiModelPricing";
import {AiExtractorType, formatAiExtractorType, OpenAiServiceTier, SupportedModel} from "../../domain/AiExtractor";
import {useForm} from "antd/es/form/Form";
import CreditsValue from "../common/CreditsValue";

function AiModelPricingDetail() {
    const appContext = useContext(AppContextContext);
    const aiModelPricingService = useContext(AiModelPricingServiceContext);
    const aiExtractorService = useContext(AiExtractorServiceContext);
    const navigate = useNavigate();
    const {aiModelPricingId} = useParams<{ aiModelPricingId: string }>();
    const [pricing, setPricing] = useState<AiModelPricing | undefined>();
    const [editMode, setEditMode] = useState(false);
    const [supportedModels, setSupportedModels] = useState<SupportedModel[]>([]);
    const [form] = useForm();
    const selectedAiExtractorType = Form.useWatch("aiExtractorType", form);
    const priceInputProps = {min: 0, max: 1000, step: 0.0001, stringMode: true, addonAfter: "credits", style: {width: "100%"}};
    const selectedTypeSupportsNativeWebSearchPricing = supportsNativeWebSearchPricing(selectedAiExtractorType);

    const layout = {
        labelCol: {xs: {span: 24}, sm: {span: 8}},
        wrapperCol: {xs: {span: 24}, sm: {span: 16}},
    };

    const tailLayout = {
        wrapperCol: {xs: {span: 24}, sm: {offset: 8, span: 16}},
    };

    const title = aiModelPricingId ? (pricing?.model || "AI model credits") : "New AI model credits";

    useEffect(() => {
        if (aiModelPricingId) {
            reload();
        } else {
            setEditMode(true);
        }
    }, [aiModelPricingId]);

    useEffect(() => {
        if (!selectedAiExtractorType) {
            setSupportedModels([]);
            return;
        }

        aiExtractorService.getSupportedModels()
            .then(data => setSupportedModels(data[selectedAiExtractorType]?.models || []));
    }, [aiExtractorService, selectedAiExtractorType]);

    if (aiModelPricingId && !pricing) {
        return <Skeleton/>;
    }

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}
                            items={[
                                {title: appContext.config?.appName},
                                {title: <Link to={"/definitions/ai-model-pricing"}>AI model credits</Link>},
                                {title}
                            ]}
                />

                <Spin spinning={aiModelPricingId ? !pricing : false} indicator={<LoadingOutlined style={{fontSize: 24}} spin={true}/>}>
                    <h1>{title}</h1>

                    {aiModelPricingId &&
                        <div className={globalStyles["common__top-button-bar"]}>
                            <Button type={"primary"} onClick={() => setEditMode(!editMode)}>Edit</Button>
                            <Button
                                danger
                                style={{marginLeft: 8}}
                                onClick={() => {
                                    Modal.confirm({
                                        content: "Do you really want to delete this AI model credit definition?",
                                        okText: "Delete",
                                        cancelText: "Cancel",
                                        okButtonProps: {danger: true},
                                        onOk: () => onDelete()
                                    });
                                }}
                            >
                                Delete
                            </Button>
                        </div>
                    }

                    <Collapse isOpen={!aiModelPricingId || editMode}>
                        {aiModelPricingId && <h3>Edit AI model credits</h3>}

                        <Form
                            {...layout}
                            form={form}
                            onFinish={onFinish}
                            className={`${globalStyles["common__form"]} ${aiModelPricingId ? globalStyles["common_form--edit"] : globalStyles["common_form--add"]}`}
                        >
                            <Form.Item name="aiExtractorType" label="Type" rules={[{required: true, message: "Type is required."}]}>
                                <Select
                                    onChange={() => {
                                        form.setFieldsValue({model: undefined});
                                    }}
                                >
                                    {Object.values(AiExtractorType).map(type => (
                                        <Select.Option key={type} value={type}>{formatAiExtractorType(type)}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item name="model" label="Model" rules={[{required: true, message: "Model is required."}]}>
                                {selectedAiExtractorType === AiExtractorType.OPENAI_COMPATIBLE_API ? (
                                    <AutoComplete
                                        options={supportedModels.map(model => ({label: model.name, value: model.name}))}
                                        filterOption={(inputValue, option) => {
                                            if (!inputValue) {
                                                return true;
                                            }

                                            return String(option?.value || "").toLowerCase().includes(inputValue.toLowerCase());
                                        }}
                                        placeholder={"Select or type model"}
                                    >
                                        <Input allowClear maxLength={100}/>
                                    </AutoComplete>
                                ) : (
                                    <Select
                                        showSearch
                                        optionFilterProp="label"
                                        placeholder={"Select model"}
                                        options={supportedModels.map(model => ({value: model.name, label: model.name}))}
                                    />
                                )}
                            </Form.Item>

                            {selectedAiExtractorType === AiExtractorType.OPENAI_API &&
                                <Form.Item name="openAiServiceTier" label="Service tier" rules={[{required: true, message: "Service tier is required."}]}>
                                    <Select
                                        options={Object.values(OpenAiServiceTier).map(value => ({
                                            value,
                                            label: value.toLowerCase()
                                        }))}
                                    />
                                </Form.Item>
                            }

                            <Form.Item name="inputPricePerMillionTokens" label="Input / 1M tokens" rules={[{required: true, message: "Input credits are required."}]}>
                                <InputNumber {...priceInputProps}/>
                            </Form.Item>

                            <Form.Item name="cachedInputPricePerMillionTokens" label="Cached input / 1M tokens">
                                <InputNumber {...priceInputProps}/>
                            </Form.Item>

                            <Form.Item name="cacheWriteInputPricePerMillionTokens" label="Cache write input / 1M tokens">
                                <InputNumber {...priceInputProps}/>
                            </Form.Item>

                            <Form.Item name="outputPricePerMillionTokens" label="Output / 1M tokens" rules={[{required: true, message: "Output credits are required."}]}>
                                <InputNumber {...priceInputProps}/>
                            </Form.Item>

                            {selectedTypeSupportsNativeWebSearchPricing &&
                                <Form.Item name="webSearchPricePer1kCalls" label="Native web search / 1K" rules={[{required: true, message: "Native web search credits are required."}]}>
                                    <InputNumber {...priceInputProps}/>
                                </Form.Item>
                            }

                            <Form.Item {...tailLayout} className={globalStyles["common__form-buttons"]}>
                                <Button type="primary" htmlType="submit">Save</Button>
                                <Button
                                    style={{marginLeft: 8}}
                                    onClick={() => {
                                        if (aiModelPricingId) {
                                            setEditMode(false);
                                            if (pricing) {
                                                form.setFieldsValue(pricing);
                                            }
                                        } else {
                                            navigate("/definitions/ai-model-pricing");
                                        }
                                    }}
                                >
                                    Cancel
                                </Button>
                            </Form.Item>
                        </Form>
                    </Collapse>

                    {aiModelPricingId && pricing &&
                        <Descriptions column={1} bordered={true} size="small" className={globalStyles.details}>
                            <Descriptions.Item label="Type">{formatAiExtractorType(pricing.aiExtractorType)}</Descriptions.Item>
                            <Descriptions.Item label="Model">{pricing.model}</Descriptions.Item>
                            {pricing.aiExtractorType === AiExtractorType.OPENAI_API &&
                                <Descriptions.Item label="Service tier">{String(pricing.openAiServiceTier).toLowerCase()}</Descriptions.Item>
                            }
                            <Descriptions.Item label="Input per 1M"><CreditsValue value={pricing.inputPricePerMillionTokens} fixedDecimals={4}/></Descriptions.Item>
                            <Descriptions.Item label="Cached input per 1M"><CreditsValue value={pricing.cachedInputPricePerMillionTokens} fixedDecimals={4}/></Descriptions.Item>
                            <Descriptions.Item label="Cache write input per 1M"><CreditsValue value={pricing.cacheWriteInputPricePerMillionTokens} fixedDecimals={4}/></Descriptions.Item>
                            <Descriptions.Item label="Output per 1M"><CreditsValue value={pricing.outputPricePerMillionTokens} fixedDecimals={4}/></Descriptions.Item>
                            {supportsNativeWebSearchPricing(pricing.aiExtractorType) &&
                                <Descriptions.Item label="Native web search per 1K">
                                    <CreditsValue value={pricing.webSearchPricePer1kCalls} fixedDecimals={4}/>
                                </Descriptions.Item>
                            }
                        </Descriptions>
                    }
                </Spin>
            </>
        </DocumentTitle>
    );

    function reload() {
        if (!aiModelPricingId) {
            return;
        }

        aiModelPricingService.get(aiModelPricingId)
            .then(value => {
                setPricing(value);
                form.setFieldsValue(value);
            });
    }

    function onFinish(values: any) {
        const normalizedValues = {
            ...values,
            openAiServiceTier: values.aiExtractorType === AiExtractorType.OPENAI_API ? values.openAiServiceTier : null,
            webSearchPricePer1kCalls: supportsNativeWebSearchPricing(values.aiExtractorType)
                ? values.webSearchPricePer1kCalls
                : null
        };

        if (aiModelPricingId) {
            aiModelPricingService.update(aiModelPricingId, normalizedValues)
                .then(() => {
                    message.success("AI model credits updated.");
                    setEditMode(false);
                    reload();
                });
        } else {
            aiModelPricingService.add(normalizedValues)
                .then(() => {
                    message.success("AI model credits added.");
                    navigate("/definitions/ai-model-pricing");
                });
        }
    }

    function onDelete() {
        if (!pricing) {
            return;
        }

        aiModelPricingService.delete(pricing)
            .then(() => {
                message.success("AI model credits deleted.");
                navigate("/definitions/ai-model-pricing");
            });
    }
}

function supportsNativeWebSearchPricing(type?: AiExtractorType) {
    return type === AiExtractorType.OPENAI_API || type === AiExtractorType.OPENAI_COMPATIBLE_API;
}

export default AiModelPricingDetail;
